import { useMutation } from "@tanstack/react-query";
import { useContext } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchGraphQL } from "../../Graphql/utils";
import { toOmsPaymentMethod } from "../../OrderMFE/Payment/enums/paymentMethod";
import {
  ALERT_BANNER_CODE,
  ALERT_BANNER_CODE_SEVERITY,
  API_RESPONSE_CODE,
  AlertBannerContext
} from "@kivunova/kivufrontendcommon";
import { RootState } from "../../store/store";
import {
  setInvoiceList,
  setInvoiceDetail,
  setInvoicePaymentList,
  setInvoicePaymentDetail
} from "../../store/slices/invoiceSlice";
import { addReceipt } from "../../store/slices/receiptSlice";
import {
  listInvoicesQuery,
  listInvoicesByStoreQuery,
  getInvoiceByOrderQuery,
  getInvoiceByIdQuery,
  listInvoicePaymentsQuery,
  listInvoicePaymentsByOrderQuery,
  listInvoicePaymentsByCompanyQuery,
  listInvoicePaymentsByStoreQuery,
  getInvoicePaymentByIdQuery,
  issueInvoiceMutation,
  updateInvoiceMutation,
  recordPaymentMutation,
  updateInvoicePaymentMutation,
  deleteInvoicePaymentMutation
} from "../../Graphql/querries/invoiceQuery";
import {
  Invoice,
  OrderPayment,
  // Input types — only what actually exists in the types file
  ListInvoicesInput,
  GetInvoiceByOrderInput,
  GetInvoiceByIdInput,
  IssueInvoiceInput,
  UpdateInvoiceInput,
  RecordPaymentInput,
  ListOrderPaymentsInput,
  GetOrderPaymentsByOrderInput,
  GetOrderPaymentByIdInput,
  UpdateOrderPaymentInput,
  DeleteOrderPaymentInput,
  // Response types
  ListInvoicesResponse,
  ListInvoicesByStoreResponse,
  GetInvoiceByOrderResponse,
  GetInvoiceByIdResponse,
  IssueInvoiceResponse,
  UpdateInvoiceResponse,
  RecordPaymentResponse,
  ListOrderPaymentsResponse,
  ListOrderPaymentsByOrderResponse,
  ListOrderPaymentsByCompanyResponse,
  ListOrderPaymentsByStoreResponse,
  GetOrderPaymentByIdResponse,
  UpdateOrderPaymentResponse,
  DeletePaymentResponse
} from "../../OrderMFE/Invoice/types/invoice";

import {
  LOG_LEVEL,
  buildLogContext,
  captureAppException,
  sendAppLog
} from "../../Utils/appLogger";

// ─── List Invoices ─────────────────────────────────────────────────────────────

export const useListInvoices = () => {
  const { setAlert } = useContext(AlertBannerContext);
  const dispatch = useDispatch();
  const userDetails = useSelector((state: RootState) => state.user.userDetails);

  return useMutation<Invoice[], Error, ListInvoicesInput | void>({
    mutationKey: ["listInvoices"],
    mutationFn: async (input) => {
      const userId = userDetails?.id;
      const companyId = userDetails?.companyId;

      sendAppLog(
        LOG_LEVEL.INFO,
        "Started fetching invoice list",
        buildLogContext(
          "invoices",
          "list-invoices",
          userId,
          companyId,
          undefined
        )
      );

      try {
        const response: ListInvoicesResponse = await fetchGraphQL(
          listInvoicesQuery,
          { input: input ?? {} },
          setAlert
        );
        const { invoices, code } = response.listInvoices;

        if (code !== API_RESPONSE_CODE.OK) {
          sendAppLog(
            LOG_LEVEL.WARNING,
            "Failed to fetch invoice list",
            buildLogContext(
              "invoices",
              "list-invoices",
              userId,
              companyId,
              undefined,
              {
                apiCode: String(code)
              }
            )
          );
          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code: code as unknown as ALERT_BANNER_CODE
          });
          return [];
        }

        if (!invoices) {
          captureAppException(
            "API returned OK but invoices array missing",
            new Error("API returned OK but invoices array missing"),
            buildLogContext(
              "invoices",
              "list-invoices",
              userId,
              companyId,
              undefined
            )
          );
          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code: ALERT_BANNER_CODE.INTERNAL_ERROR
          });
          return [];
        }

        dispatch(setInvoiceList(invoices));
        sendAppLog(
          LOG_LEVEL.INFO,
          "Invoice list fetched successfully",
          buildLogContext(
            "invoices",
            "list-invoices",
            userId,
            companyId,
            undefined,
            {
              invoiceCount: invoices.length
            }
          )
        );
        return invoices;
      } catch (error) {
        captureAppException(
          "Failed to fetch invoice list",
          error,
          buildLogContext(
            "invoices",
            "list-invoices",
            userId,
            companyId,
            undefined
          )
        );
        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: ALERT_BANNER_CODE.INTERNAL_ERROR
        });
        return [];
      }
    }
  });
};

export const useListInvoicesByStore = () => {
  const { setAlert } = useContext(AlertBannerContext);
  const dispatch = useDispatch();
  const userDetails = useSelector((state: RootState) => state.user.userDetails);

  return useMutation<Invoice[], Error, void>({
    mutationKey: ["listInvoicesByStore"],
    mutationFn: async () => {
      const userId = userDetails?.id;
      const companyId = userDetails?.companyId;
      const storeId = userDetails?.storeId;

      sendAppLog(
        LOG_LEVEL.INFO,
        "Started fetching store invoice list",
        buildLogContext(
          "invoices",
          "list-invoices-by-store",
          userId,
          companyId,
          storeId
        )
      );

      try {
        const response: ListInvoicesByStoreResponse = await fetchGraphQL(
          listInvoicesByStoreQuery,
          {},
          setAlert
        );
        const { invoices, code } = response.listInvoicesByStore;

        if (code !== API_RESPONSE_CODE.OK) {
          sendAppLog(
            LOG_LEVEL.WARNING,
            "Failed to fetch store invoice list",
            buildLogContext(
              "invoices",
              "list-invoices-by-store",
              userId,
              companyId,
              storeId,
              {
                apiCode: String(code)
              }
            )
          );
          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code: code as unknown as ALERT_BANNER_CODE
          });
          return [];
        }

        if (!invoices) {
          captureAppException(
            "API returned OK but store invoices array missing",
            new Error("API returned OK but store invoices array missing"),
            buildLogContext(
              "invoices",
              "list-invoices-by-store",
              userId,
              companyId,
              storeId
            )
          );
          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code: ALERT_BANNER_CODE.INTERNAL_ERROR
          });
          return [];
        }

        dispatch(setInvoiceList(invoices));
        sendAppLog(
          LOG_LEVEL.INFO,
          "Store invoice list fetched successfully",
          buildLogContext(
            "invoices",
            "list-invoices-by-store",
            userId,
            companyId,
            storeId,
            {
              invoiceCount: invoices.length
            }
          )
        );
        return invoices;
      } catch (error) {
        captureAppException(
          "Failed to fetch store invoice list",
          error,
          buildLogContext(
            "invoices",
            "list-invoices-by-store",
            userId,
            companyId,
            storeId
          )
        );
        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: ALERT_BANNER_CODE.INTERNAL_ERROR
        });
        return [];
      }
    }
  });
};

// ─── Get Invoice ───────────────────────────────────────────────────────────────

export const useGetInvoiceByOrder = () => {
  const { setAlert } = useContext(AlertBannerContext);
  const dispatch = useDispatch();
  const userDetails = useSelector((state: RootState) => state.user.userDetails);

  return useMutation<Invoice | null, Error, GetInvoiceByOrderInput>({
    mutationKey: ["getInvoiceByOrder"],
    mutationFn: async (input) => {
      const userId = userDetails?.id;
      const companyId = userDetails?.companyId;

      sendAppLog(
        LOG_LEVEL.INFO,
        "Started fetching invoice by order",
        buildLogContext(
          "invoices",
          "get-invoice-by-order",
          userId,
          companyId,
          undefined,
          {
            orderId: input.orderId
          }
        )
      );

      try {
        const response: GetInvoiceByOrderResponse = await fetchGraphQL(
          getInvoiceByOrderQuery,
          { input },
          setAlert
        );
        const { invoice, code } = response.getInvoiceByOrder;

        if (code !== API_RESPONSE_CODE.OK) {
          sendAppLog(
            LOG_LEVEL.WARNING,
            "Failed to fetch invoice by order",
            buildLogContext(
              "invoices",
              "get-invoice-by-order",
              userId,
              companyId,
              undefined,
              {
                apiCode: String(code),
                orderId: input.orderId
              }
            )
          );
          // NOT_FOUND or INVOICE_NOT_FOUND are expected cases (order has no invoice yet) —
          // skip the error alert so the UI does not flash a spurious error banner.
          if (
            code !== API_RESPONSE_CODE.NOT_FOUND &&
            code !== "INVOICE_NOT_FOUND"
          ) {
            setAlert({
              severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
              code: code as unknown as ALERT_BANNER_CODE
            });
          }
          return null;
        }

        if (!invoice) {
          captureAppException(
            "API returned OK but invoice object missing",
            new Error("API returned OK but invoice object missing"),
            buildLogContext(
              "invoices",
              "get-invoice-by-order",
              userId,
              companyId,
              undefined,
              {
                orderId: input.orderId
              }
            )
          );
          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code: ALERT_BANNER_CODE.INTERNAL_ERROR
          });
          return null;
        }

        dispatch(setInvoiceDetail(invoice));
        sendAppLog(
          LOG_LEVEL.INFO,
          "Invoice fetched by order successfully",
          buildLogContext(
            "invoices",
            "get-invoice-by-order",
            userId,
            companyId,
            undefined,
            {
              invoiceId: invoice.invoiceId,
              orderId: input.orderId
            }
          )
        );
        return invoice;
      } catch (error) {
        captureAppException(
          "Failed to fetch invoice by order",
          error,
          buildLogContext(
            "invoices",
            "get-invoice-by-order",
            userId,
            companyId,
            undefined,
            {
              orderId: input.orderId
            }
          )
        );
        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: ALERT_BANNER_CODE.INTERNAL_ERROR
        });
        return null;
      }
    }
  });
};

export const useGetInvoiceById = () => {
  const { setAlert } = useContext(AlertBannerContext);
  const dispatch = useDispatch();
  const userDetails = useSelector((state: RootState) => state.user.userDetails);

  return useMutation<Invoice | null, Error, GetInvoiceByIdInput>({
    mutationKey: ["getInvoiceById"],
    mutationFn: async (input) => {
      const userId = userDetails?.id;
      const companyId = userDetails?.companyId;

      sendAppLog(
        LOG_LEVEL.INFO,
        "Started fetching invoice by id",
        buildLogContext(
          "invoices",
          "get-invoice-by-id",
          userId,
          companyId,
          undefined,
          {
            invoiceId: input.invoiceId
          }
        )
      );

      try {
        const response: GetInvoiceByIdResponse = await fetchGraphQL(
          getInvoiceByIdQuery,
          { input },
          setAlert
        );
        const { invoice, code } = response.getInvoiceById;

        if (code !== API_RESPONSE_CODE.OK) {
          sendAppLog(
            LOG_LEVEL.WARNING,
            "Failed to fetch invoice by id",
            buildLogContext(
              "invoices",
              "get-invoice-by-id",
              userId,
              companyId,
              undefined,
              {
                apiCode: String(code),
                invoiceId: input.invoiceId
              }
            )
          );
          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code: code as unknown as ALERT_BANNER_CODE
          });
          return null;
        }

        if (!invoice) {
          captureAppException(
            "API returned OK but invoice object missing",
            new Error("API returned OK but invoice object missing"),
            buildLogContext(
              "invoices",
              "get-invoice-by-id",
              userId,
              companyId,
              undefined,
              {
                invoiceId: input.invoiceId
              }
            )
          );
          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code: ALERT_BANNER_CODE.INTERNAL_ERROR
          });
          return null;
        }

        dispatch(setInvoiceDetail(invoice));
        sendAppLog(
          LOG_LEVEL.INFO,
          "Invoice fetched by id successfully",
          buildLogContext(
            "invoices",
            "get-invoice-by-id",
            userId,
            companyId,
            undefined,
            {
              invoiceId: invoice.invoiceId
            }
          )
        );
        return invoice;
      } catch (error) {
        captureAppException(
          "Failed to fetch invoice by id",
          error,
          buildLogContext(
            "invoices",
            "get-invoice-by-id",
            userId,
            companyId,
            undefined,
            {
              invoiceId: input.invoiceId
            }
          )
        );
        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: ALERT_BANNER_CODE.INTERNAL_ERROR
        });
        return null;
      }
    }
  });
};

// ─── Issue / Update Invoice ────────────────────────────────────────────────────

export const useIssueInvoice = () => {
  const { setAlert } = useContext(AlertBannerContext);
  const dispatch = useDispatch();
  const userDetails = useSelector((state: RootState) => state.user.userDetails);

  return useMutation<Invoice | null, Error, IssueInvoiceInput>({
    mutationKey: ["issueInvoice"],
    mutationFn: async (input) => {
      const userId = userDetails?.id;
      const companyId = userDetails?.companyId;

      sendAppLog(
        LOG_LEVEL.INFO,
        "Started issuing invoice",
        buildLogContext(
          "invoices",
          "issue-invoice",
          userId,
          companyId,
          undefined,
          {
            orderId: input.orderId,
            issueDate: input.issueDate as string
          }
        )
      );

      try {
        const response: IssueInvoiceResponse = await fetchGraphQL(
          issueInvoiceMutation,
          { input },
          setAlert
        );
        const { invoice, code } = response.issueInvoice;

        if (code !== API_RESPONSE_CODE.OK) {
          sendAppLog(
            LOG_LEVEL.WARNING,
            "Issue invoice rejected by API",
            buildLogContext(
              "invoices",
              "issue-invoice",
              userId,
              companyId,
              undefined,
              {
                apiCode: String(code),
                orderId: input.orderId
              }
            )
          );
          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code: code as unknown as ALERT_BANNER_CODE
          });
          return null;
        }

        if (!invoice) {
          captureAppException(
            "API returned OK but issued invoice missing",
            new Error("API returned OK but issued invoice missing"),
            buildLogContext(
              "invoices",
              "issue-invoice",
              userId,
              companyId,
              undefined,
              {
                orderId: input.orderId
              }
            )
          );
          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code: ALERT_BANNER_CODE.INTERNAL_ERROR
          });
          return null;
        }

        dispatch(setInvoiceDetail(invoice));
        sendAppLog(
          LOG_LEVEL.INFO,
          "Invoice issued successfully",
          buildLogContext(
            "invoices",
            "issue-invoice",
            userId,
            companyId,
            undefined,
            {
              invoiceId: invoice.invoiceId,
              orderId: input.orderId
            }
          )
        );
        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.SUCCESS,
          code: ALERT_BANNER_CODE.OK
        });
        return invoice;
      } catch (error) {
        captureAppException(
          "Failed to issue invoice",
          error,
          buildLogContext(
            "invoices",
            "issue-invoice",
            userId,
            companyId,
            undefined,
            {
              orderId: input.orderId
            }
          )
        );
        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: ALERT_BANNER_CODE.INTERNAL_ERROR
        });
        return null;
      }
    }
  });
};

export const useUpdateInvoice = () => {
  const { setAlert } = useContext(AlertBannerContext);
  const dispatch = useDispatch();
  const userDetails = useSelector((state: RootState) => state.user.userDetails);

  return useMutation<Invoice | null, Error, UpdateInvoiceInput>({
    mutationKey: ["updateInvoice"],
    mutationFn: async (input) => {
      const userId = userDetails?.id;
      const companyId = userDetails?.companyId;

      sendAppLog(
        LOG_LEVEL.INFO,
        "Started updating invoice",
        buildLogContext(
          "invoices",
          "update-invoice",
          userId,
          companyId,
          undefined,
          {
            invoiceId: input.invoiceId
          }
        )
      );

      try {
        const response: UpdateInvoiceResponse = await fetchGraphQL(
          updateInvoiceMutation,
          { input },
          setAlert
        );
        const { invoice, code } = response.updateInvoice;

        if (code !== API_RESPONSE_CODE.OK) {
          sendAppLog(
            LOG_LEVEL.WARNING,
            "Update invoice rejected by API",
            buildLogContext(
              "invoices",
              "update-invoice",
              userId,
              companyId,
              undefined,
              {
                apiCode: String(code),
                invoiceId: input.invoiceId
              }
            )
          );
          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code: code as unknown as ALERT_BANNER_CODE
          });
          return null;
        }

        if (!invoice) {
          captureAppException(
            "API returned OK but updated invoice missing",
            new Error("API returned OK but updated invoice missing"),
            buildLogContext(
              "invoices",
              "update-invoice",
              userId,
              companyId,
              undefined,
              {
                invoiceId: input.invoiceId
              }
            )
          );
          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code: ALERT_BANNER_CODE.INTERNAL_ERROR
          });
          return null;
        }

        dispatch(setInvoiceDetail(invoice));
        sendAppLog(
          LOG_LEVEL.INFO,
          "Invoice updated successfully",
          buildLogContext(
            "invoices",
            "update-invoice",
            userId,
            companyId,
            undefined,
            {
              invoiceId: invoice.invoiceId
            }
          )
        );
        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.SUCCESS,
          code: ALERT_BANNER_CODE.OK
        });
        return invoice;
      } catch (error) {
        captureAppException(
          "Failed to update invoice",
          error,
          buildLogContext(
            "invoices",
            "update-invoice",
            userId,
            companyId,
            undefined,
            {
              invoiceId: input.invoiceId
            }
          )
        );
        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: ALERT_BANNER_CODE.INTERNAL_ERROR
        });
        return null;
      }
    }
  });
};

export const useRecordPayment = () => {
  const { setAlert } = useContext(AlertBannerContext);
  const dispatch = useDispatch();
  const userDetails = useSelector((state: RootState) => state.user.userDetails);

  return useMutation<Invoice | null, Error, RecordPaymentInput>({
    mutationKey: ["recordPayment"],
    mutationFn: async (input) => {
      const userId = userDetails?.id;
      const companyId = userDetails?.companyId;

      const apiInput: RecordPaymentInput = {
        invoiceId: input.invoiceId,
        amount: input.amount,
        paymentDate: input.paymentDate,
        reference: input.reference,
        paymentMethod: toOmsPaymentMethod(input.paymentMethod)
      };

      sendAppLog(
        LOG_LEVEL.INFO,
        "Started recording payment",
        buildLogContext(
          "invoices",
          "record-payment",
          userId,
          companyId,
          undefined,
          {
            invoiceId: apiInput.invoiceId,
            amount: apiInput.amount,
            paymentMethod: apiInput.paymentMethod as string
          }
        )
      );

      try {
        const response: RecordPaymentResponse = await fetchGraphQL(
          recordPaymentMutation,
          { input: apiInput },
          setAlert
        );
        const { code, invoice, receipt } = response.recordPayment;

        if (code !== API_RESPONSE_CODE.OK) {
          sendAppLog(
            LOG_LEVEL.WARNING,
            "Record payment rejected by API",
            buildLogContext(
              "invoices",
              "record-payment",
              userId,
              companyId,
              undefined,
              {
                apiCode: String(code),
                invoiceId: input.invoiceId
              }
            )
          );
          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code: code as unknown as ALERT_BANNER_CODE
          });
          return null;
        }

        if (!invoice) {
          captureAppException(
            "API returned OK but invoice missing after payment",
            new Error("API returned OK but invoice missing after payment"),
            buildLogContext(
              "invoices",
              "record-payment",
              userId,
              companyId,
              undefined,
              {
                invoiceId: input.invoiceId
              }
            )
          );
          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code: ALERT_BANNER_CODE.INTERNAL_ERROR
          });
          return null;
        }

        dispatch(setInvoiceDetail(invoice));
        if (receipt) {
          dispatch(addReceipt(receipt));
        }
        sendAppLog(
          LOG_LEVEL.INFO,
          "Payment recorded successfully",
          buildLogContext(
            "invoices",
            "record-payment",
            userId,
            companyId,
            undefined,
            {
              invoiceId: invoice.invoiceId,
              receiptId: receipt?.receiptId ?? "",
              paidAmount: invoice.paidAmount,
              remainingAmount: invoice.remainingAmount
            }
          )
        );
        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.SUCCESS,
          code: ALERT_BANNER_CODE.OK
        });
        return invoice;
      } catch (error) {
        captureAppException(
          "Failed to record payment",
          error,
          buildLogContext(
            "invoices",
            "record-payment",
            userId,
            companyId,
            undefined,
            {
              invoiceId: input.invoiceId
            }
          )
        );
        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: ALERT_BANNER_CODE.INTERNAL_ERROR
        });
        return null;
      }
    }
  });
};

export const useListInvoicePayments = () => {
  const { setAlert } = useContext(AlertBannerContext);
  const dispatch = useDispatch();
  const userDetails = useSelector((state: RootState) => state.user.userDetails);

  return useMutation<OrderPayment[], Error, ListOrderPaymentsInput>({
    mutationKey: ["listInvoicePayments"],
    mutationFn: async (input) => {
      const userId = userDetails?.id;
      const companyId = userDetails?.companyId;

      sendAppLog(
        LOG_LEVEL.INFO,
        "Started fetching invoice payments",
        buildLogContext(
          "invoices",
          "list-invoice-payments",
          userId,
          companyId,
          undefined,
          {
            invoiceId: input.invoiceId
          }
        )
      );

      try {
        const response: ListOrderPaymentsResponse = await fetchGraphQL(
          listInvoicePaymentsQuery,
          { input },
          setAlert
        );
        const { payments, code } = response.listInvoicePayments;

        if (code !== API_RESPONSE_CODE.OK) {
          sendAppLog(
            LOG_LEVEL.WARNING,
            "Failed to fetch invoice payments",
            buildLogContext(
              "invoices",
              "list-invoice-payments",
              userId,
              companyId,
              undefined,
              {
                apiCode: String(code),
                invoiceId: input.invoiceId
              }
            )
          );
          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code: code as unknown as ALERT_BANNER_CODE
          });
          return [];
        }

        if (!payments) {
          captureAppException(
            "API returned OK but payments array missing",
            new Error("API returned OK but payments array missing"),
            buildLogContext(
              "invoices",
              "list-invoice-payments",
              userId,
              companyId,
              undefined,
              {
                invoiceId: input.invoiceId
              }
            )
          );
          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code: ALERT_BANNER_CODE.INTERNAL_ERROR
          });
          return [];
        }

        dispatch(setInvoicePaymentList(payments));
        sendAppLog(
          LOG_LEVEL.INFO,
          "Invoice payments fetched successfully",
          buildLogContext(
            "invoices",
            "list-invoice-payments",
            userId,
            companyId,
            undefined,
            {
              invoiceId: input.invoiceId,
              paymentCount: payments.length
            }
          )
        );
        return payments;
      } catch (error) {
        captureAppException(
          "Failed to fetch invoice payments",
          error,
          buildLogContext(
            "invoices",
            "list-invoice-payments",
            userId,
            companyId,
            undefined,
            {
              invoiceId: input.invoiceId
            }
          )
        );
        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: ALERT_BANNER_CODE.INTERNAL_ERROR
        });
        return [];
      }
    }
  });
};

export const useListInvoicePaymentsByOrder = () => {
  const { setAlert } = useContext(AlertBannerContext);
  const dispatch = useDispatch();
  const userDetails = useSelector((state: RootState) => state.user.userDetails);

  return useMutation<OrderPayment[], Error, GetOrderPaymentsByOrderInput>({
    mutationKey: ["listInvoicePaymentsByOrder"],
    mutationFn: async (input) => {
      const userId = userDetails?.id;
      const companyId = userDetails?.companyId;

      sendAppLog(
        LOG_LEVEL.INFO,
        "Started fetching payments by order",
        buildLogContext(
          "invoices",
          "list-invoice-payments-by-order",
          userId,
          companyId,
          undefined,
          {
            orderId: input.orderId
          }
        )
      );

      try {
        const response: ListOrderPaymentsByOrderResponse = await fetchGraphQL(
          listInvoicePaymentsByOrderQuery,
          { input },
          setAlert
        );
        const { payments, code } = response.listInvoicePaymentsByOrder;

        if (code !== API_RESPONSE_CODE.OK) {
          sendAppLog(
            LOG_LEVEL.WARNING,
            "Failed to fetch payments by order",
            buildLogContext(
              "invoices",
              "list-invoice-payments-by-order",
              userId,
              companyId,
              undefined,
              {
                apiCode: String(code),
                orderId: input.orderId
              }
            )
          );
          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code: code as unknown as ALERT_BANNER_CODE
          });
          return [];
        }

        if (!payments) {
          captureAppException(
            "API returned OK but payments array missing",
            new Error("API returned OK but payments array missing"),
            buildLogContext(
              "invoices",
              "list-invoice-payments-by-order",
              userId,
              companyId,
              undefined,
              {
                orderId: input.orderId
              }
            )
          );
          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code: ALERT_BANNER_CODE.INTERNAL_ERROR
          });
          return [];
        }

        dispatch(setInvoicePaymentList(payments));
        sendAppLog(
          LOG_LEVEL.INFO,
          "Payments by order fetched successfully",
          buildLogContext(
            "invoices",
            "list-invoice-payments-by-order",
            userId,
            companyId,
            undefined,
            {
              orderId: input.orderId,
              paymentCount: payments.length
            }
          )
        );
        return payments;
      } catch (error) {
        captureAppException(
          "Failed to fetch payments by order",
          error,
          buildLogContext(
            "invoices",
            "list-invoice-payments-by-order",
            userId,
            companyId,
            undefined,
            {
              orderId: input.orderId
            }
          )
        );
        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: ALERT_BANNER_CODE.INTERNAL_ERROR
        });
        return [];
      }
    }
  });
};

export const useListInvoicePaymentsByCompany = () => {
  const { setAlert } = useContext(AlertBannerContext);
  const dispatch = useDispatch();
  const userDetails = useSelector((state: RootState) => state.user.userDetails);

  return useMutation<OrderPayment[], Error, void>({
    mutationKey: ["listInvoicePaymentsByCompany"],
    mutationFn: async () => {
      const userId = userDetails?.id;
      const companyId = userDetails?.companyId;

      sendAppLog(
        LOG_LEVEL.INFO,
        "Started fetching company-wide invoice payments",
        buildLogContext(
          "invoices",
          "list-invoice-payments-by-company",
          userId,
          companyId,
          undefined
        )
      );

      try {
        const response: ListOrderPaymentsByCompanyResponse = await fetchGraphQL(
          listInvoicePaymentsByCompanyQuery,
          {},
          setAlert
        );
        const { payments, code } = response.listInvoicePaymentsByCompany;

        if (code !== API_RESPONSE_CODE.OK) {
          sendAppLog(
            LOG_LEVEL.WARNING,
            "Failed to fetch company invoice payments",
            buildLogContext(
              "invoices",
              "list-invoice-payments-by-company",
              userId,
              companyId,
              undefined,
              {
                apiCode: String(code)
              }
            )
          );
          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code: code as unknown as ALERT_BANNER_CODE
          });
          return [];
        }

        if (!payments) {
          captureAppException(
            "API returned OK but company payments array missing",
            new Error("API returned OK but company payments array missing"),
            buildLogContext(
              "invoices",
              "list-invoice-payments-by-company",
              userId,
              companyId,
              undefined
            )
          );
          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code: ALERT_BANNER_CODE.INTERNAL_ERROR
          });
          return [];
        }

        dispatch(setInvoicePaymentList(payments));
        sendAppLog(
          LOG_LEVEL.INFO,
          "Company invoice payments fetched successfully",
          buildLogContext(
            "invoices",
            "list-invoice-payments-by-company",
            userId,
            companyId,
            undefined,
            {
              paymentCount: payments.length
            }
          )
        );
        return payments;
      } catch (error) {
        captureAppException(
          "Failed to fetch company invoice payments",
          error,
          buildLogContext(
            "invoices",
            "list-invoice-payments-by-company",
            userId,
            companyId,
            undefined
          )
        );
        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: ALERT_BANNER_CODE.INTERNAL_ERROR
        });
        return [];
      }
    }
  });
};

export const useListInvoicePaymentsByStore = () => {
  const { setAlert } = useContext(AlertBannerContext);
  const dispatch = useDispatch();
  const userDetails = useSelector((state: RootState) => state.user.userDetails);

  return useMutation<OrderPayment[], Error, void>({
    mutationKey: ["listInvoicePaymentsByStore"],
    mutationFn: async () => {
      const userId = userDetails?.id;
      const companyId = userDetails?.companyId;
      const storeId = userDetails?.storeId;

      sendAppLog(
        LOG_LEVEL.INFO,
        "Started fetching store invoice payments",
        buildLogContext(
          "invoices",
          "list-invoice-payments-by-store",
          userId,
          companyId,
          storeId
        )
      );

      try {
        const response: ListOrderPaymentsByStoreResponse = await fetchGraphQL(
          listInvoicePaymentsByStoreQuery,
          {},
          setAlert
        );
        const { payments, code } = response.listInvoicePaymentsByStore;

        if (code !== API_RESPONSE_CODE.OK) {
          sendAppLog(
            LOG_LEVEL.WARNING,
            "Failed to fetch store invoice payments",
            buildLogContext(
              "invoices",
              "list-invoice-payments-by-store",
              userId,
              companyId,
              storeId,
              {
                apiCode: String(code)
              }
            )
          );
          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code: code as unknown as ALERT_BANNER_CODE
          });
          return [];
        }

        if (!payments) {
          captureAppException(
            "API returned OK but store payments array missing",
            new Error("API returned OK but store payments array missing"),
            buildLogContext(
              "invoices",
              "list-invoice-payments-by-store",
              userId,
              companyId,
              storeId
            )
          );
          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code: ALERT_BANNER_CODE.INTERNAL_ERROR
          });
          return [];
        }

        dispatch(setInvoicePaymentList(payments));
        sendAppLog(
          LOG_LEVEL.INFO,
          "Store invoice payments fetched successfully",
          buildLogContext(
            "invoices",
            "list-invoice-payments-by-store",
            userId,
            companyId,
            storeId,
            {
              paymentCount: payments.length
            }
          )
        );
        return payments;
      } catch (error) {
        captureAppException(
          "Failed to fetch store invoice payments",
          error,
          buildLogContext(
            "invoices",
            "list-invoice-payments-by-store",
            userId,
            companyId,
            storeId
          )
        );
        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: ALERT_BANNER_CODE.INTERNAL_ERROR
        });
        return [];
      }
    }
  });
};

export const useGetInvoicePaymentById = () => {
  const { setAlert } = useContext(AlertBannerContext);
  const dispatch = useDispatch();
  const userDetails = useSelector((state: RootState) => state.user.userDetails);

  return useMutation<OrderPayment | null, Error, GetOrderPaymentByIdInput>({
    mutationKey: ["getInvoicePaymentById"],
    mutationFn: async (input) => {
      const userId = userDetails?.id;
      const companyId = userDetails?.companyId;

      sendAppLog(
        LOG_LEVEL.INFO,
        "Started fetching invoice payment by id",
        buildLogContext(
          "invoices",
          "get-invoice-payment-by-id",
          userId,
          companyId,
          undefined,
          {
            paymentId: input.paymentId
          }
        )
      );

      try {
        const response: GetOrderPaymentByIdResponse = await fetchGraphQL(
          getInvoicePaymentByIdQuery,
          { input },
          setAlert
        );
        const { payment, code } = response.getInvoicePaymentById;

        if (code !== API_RESPONSE_CODE.OK) {
          sendAppLog(
            LOG_LEVEL.WARNING,
            "Failed to fetch invoice payment by id",
            buildLogContext(
              "invoices",
              "get-invoice-payment-by-id",
              userId,
              companyId,
              undefined,
              {
                apiCode: String(code),
                paymentId: input.paymentId
              }
            )
          );
          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code: code as unknown as ALERT_BANNER_CODE
          });
          return null;
        }

        if (!payment) {
          captureAppException(
            "API returned OK but payment object missing",
            new Error("API returned OK but payment object missing"),
            buildLogContext(
              "invoices",
              "get-invoice-payment-by-id",
              userId,
              companyId,
              undefined,
              {
                paymentId: input.paymentId
              }
            )
          );
          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code: ALERT_BANNER_CODE.INTERNAL_ERROR
          });
          return null;
        }

        dispatch(setInvoicePaymentDetail(payment));
        sendAppLog(
          LOG_LEVEL.INFO,
          "Invoice payment fetched by id successfully",
          buildLogContext(
            "invoices",
            "get-invoice-payment-by-id",
            userId,
            companyId,
            undefined,
            {
              paymentId: payment.paymentId
            }
          )
        );
        return payment;
      } catch (error) {
        captureAppException(
          "Failed to fetch invoice payment by id",
          error,
          buildLogContext(
            "invoices",
            "get-invoice-payment-by-id",
            userId,
            companyId,
            undefined,
            {
              paymentId: input.paymentId
            }
          )
        );
        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: ALERT_BANNER_CODE.INTERNAL_ERROR
        });
        return null;
      }
    }
  });
};

export const useUpdateInvoicePayment = () => {
  const { setAlert } = useContext(AlertBannerContext);
  const dispatch = useDispatch();
  const userDetails = useSelector((state: RootState) => state.user.userDetails);

  return useMutation<OrderPayment | null, Error, UpdateOrderPaymentInput>({
    mutationKey: ["updateInvoicePayment"],
    mutationFn: async (input) => {
      const userId = userDetails?.id;
      const companyId = userDetails?.companyId;

      sendAppLog(
        LOG_LEVEL.INFO,
        "Started updating invoice payment",
        buildLogContext(
          "invoices",
          "update-invoice-payment",
          userId,
          companyId,
          undefined,
          {
            paymentId: input.paymentId
          }
        )
      );

      try {
        const response: UpdateOrderPaymentResponse = await fetchGraphQL(
          updateInvoicePaymentMutation,
          { input },
          setAlert
        );
        const { payment, code } = response.updateInvoicePayment;

        if (code !== API_RESPONSE_CODE.OK) {
          sendAppLog(
            LOG_LEVEL.WARNING,
            "Update invoice payment rejected by API",
            buildLogContext(
              "invoices",
              "update-invoice-payment",
              userId,
              companyId,
              undefined,
              {
                apiCode: String(code),
                paymentId: input.paymentId
              }
            )
          );
          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code: code as unknown as ALERT_BANNER_CODE
          });
          return null;
        }

        if (!payment) {
          captureAppException(
            "API returned OK but updated payment missing",
            new Error("API returned OK but updated payment missing"),
            buildLogContext(
              "invoices",
              "update-invoice-payment",
              userId,
              companyId,
              undefined,
              {
                paymentId: input.paymentId
              }
            )
          );
          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code: ALERT_BANNER_CODE.INTERNAL_ERROR
          });
          return null;
        }

        dispatch(setInvoicePaymentDetail(payment));
        sendAppLog(
          LOG_LEVEL.INFO,
          "Invoice payment updated successfully",
          buildLogContext(
            "invoices",
            "update-invoice-payment",
            userId,
            companyId,
            undefined,
            {
              paymentId: payment.paymentId
            }
          )
        );
        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.SUCCESS,
          code: ALERT_BANNER_CODE.OK
        });
        return payment;
      } catch (error) {
        captureAppException(
          "Failed to update invoice payment",
          error,
          buildLogContext(
            "invoices",
            "update-invoice-payment",
            userId,
            companyId,
            undefined,
            {
              paymentId: input.paymentId
            }
          )
        );
        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: ALERT_BANNER_CODE.INTERNAL_ERROR
        });
        return null;
      }
    }
  });
};

export const useDeleteInvoicePayment = () => {
  const { setAlert } = useContext(AlertBannerContext);
  const userDetails = useSelector((state: RootState) => state.user.userDetails);

  return useMutation<boolean, Error, DeleteOrderPaymentInput>({
    mutationKey: ["deleteInvoicePayment"],
    mutationFn: async (input) => {
      const userId = userDetails?.id;
      const companyId = userDetails?.companyId;

      sendAppLog(
        LOG_LEVEL.INFO,
        "Started deleting invoice payment",
        buildLogContext(
          "invoices",
          "delete-invoice-payment",
          userId,
          companyId,
          undefined,
          {
            paymentId: input.paymentId
          }
        )
      );

      try {
        const response: DeletePaymentResponse = await fetchGraphQL(
          deleteInvoicePaymentMutation,
          { input },
          setAlert
        );
        const { code } = response.deleteInvoicePayment;

        if (code !== API_RESPONSE_CODE.OK) {
          sendAppLog(
            LOG_LEVEL.WARNING,
            "Delete invoice payment rejected by API",
            buildLogContext(
              "invoices",
              "delete-invoice-payment",
              userId,
              companyId,
              undefined,
              {
                apiCode: String(code),
                paymentId: input.paymentId
              }
            )
          );
          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code: code as unknown as ALERT_BANNER_CODE
          });
          return false;
        }

        sendAppLog(
          LOG_LEVEL.INFO,
          "Invoice payment deleted successfully",
          buildLogContext(
            "invoices",
            "delete-invoice-payment",
            userId,
            companyId,
            undefined,
            {
              paymentId: input.paymentId
            }
          )
        );
        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.SUCCESS,
          code: ALERT_BANNER_CODE.OK
        });
        return true;
      } catch (error) {
        captureAppException(
          "Failed to delete invoice payment",
          error,
          buildLogContext(
            "invoices",
            "delete-invoice-payment",
            userId,
            companyId,
            undefined,
            {
              paymentId: input.paymentId
            }
          )
        );
        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: ALERT_BANNER_CODE.INTERNAL_ERROR
        });
        return false;
      }
    }
  });
};
